import React, { useState } from 'react';
import { Spin, Popover } from 'antd';
// 使用 window.message 代替 antd 静态方法，避免 context 警告
import {
  CopyOutlined,
  PictureOutlined,
  WechatOutlined,
} from '@ant-design/icons';
import bg from '../../assets/bg.png';
import { uuidToBase62 } from '../../utils/base62';
import { handleCopy } from '../../utils/common';
import { useEmbeddedAuth } from '../../contexts/EmbeddedAuthContext';
import { getCurrentUser } from '../../services/authService';

const SharePopover = ({ message, userInfo, basicUserInfo, qrConfig, children }) => {
  const [visible, setVisible] = useState(false);
  const [downloading, setDownloading] = useState(false);
  const [qrUrl, setQrUrl] = useState('');
  const [loadingQr, setLoadingQr] = useState(false);
  const { showLoginModal } = useEmbeddedAuth();
  const currentUser = getCurrentUser();

  // 工具函数：加载图片为Image对象，失败兜底
  function loadImage(src, fallback) {
    return new Promise((resolve) => {
      const img = new window.Image();
      img.crossOrigin = 'anonymous';
      img.onload = () => resolve(img);
      img.onerror = () => {
        if (fallback && fallback !== src) {
          loadImage(fallback).then(resolve);
        } else {
          resolve(null);
        }
      };
      img.src = src;
    });
  }



  // 保存图片
  const handleSaveImage = async () => {
    setDownloading(true);
    try {
      // 1. 获取数据
      const avatarUrl = basicUserInfo?.avatar || '';
      const name = basicUserInfo?.name || '';
      const following = userInfo?.following || 0;
      const followers = userInfo?.followers || 0;
      const likeCount = userInfo?.like_count || 0;
      const bio = userInfo?.bio || '';
      const tags = userInfo?.tag || [];

      // 2. 创建canvas
      const width = 500;
      const height = 650;
      const canvas = document.createElement('canvas');
      canvas.width = width;
      canvas.height = height;
      const ctx = canvas.getContext('2d');

      // 圆角边框
      ctx.beginPath();
      ctx.roundRect(0, 0, width, height, 20);
      ctx.clip();
      ctx.fillStyle = '#f5f5f5'; // 默认背景色
      ctx.fillRect(0, 0, width, height);
      ctx.strokeStyle = '#e0e0e0'; // 边框颜色
      ctx.lineWidth = 2;
      ctx.stroke();

      // 3. 设置背景 bg.png
      const backgroundImg = await loadImage(bg, '/placeholder.png');
      if (backgroundImg) {
        ctx.drawImage(backgroundImg, 0, 0, width, height);
      } else {
        ctx.fillStyle = '#f5f5f5';
        ctx.fillRect(0, 0, width, height);
      }

      

      // 5. 头像 (圆形，居中在背景矩形内)
      let avatarImg = null;
      avatarImg = await loadImage(avatarUrl, '/placeholder-user.png');
 
      if (avatarImg) {
        ctx.save();
        ctx.beginPath();
        ctx.arc(width / 2, 140, 50, 0, 2 * Math.PI);
        ctx.closePath();
        ctx.clip();
        ctx.drawImage(avatarImg, width / 2 - 50, 90, 100, 100);
        ctx.restore();
      }

      // 6. 用户名 (带@符号)
      ctx.font = 'bold 28px sans-serif';
      ctx.fillStyle = '#333333';
      ctx.textAlign = 'center';
      ctx.fillText(`@${name}`, width / 2, 270);

      // 7. 统计数据 (关注/粉丝/获赞)
      const statsY = 320;
      const statsLabelY = 345;
      ctx.font = 'bold 24px sans-serif';
      ctx.fillStyle = '#333333';
      ctx.textAlign = 'center';
      ctx.fillText(`${followers}`, width / 2 - 120, statsY);
      ctx.font = '16px sans-serif';
      ctx.fillStyle = '#666666';
      ctx.fillText('好友', width / 2 - 120, statsLabelY);
      ctx.font = 'bold 24px sans-serif';  
      ctx.fillStyle = '#333333';
      ctx.fillText(``, width / 2, statsY);
      ctx.font = '16px sans-serif';
      ctx.fillStyle = '#666666';
      ctx.fillText('', width / 2, statsLabelY);
      ctx.font = 'bold 24px sans-serif';
      ctx.fillStyle = '#333333';
      ctx.fillText(`${likeCount}`, width / 2 + 120, statsY);
      ctx.font = '16px sans-serif';
      ctx.fillStyle = '#666666';
      ctx.fillText('获赞', width / 2 + 120, statsLabelY);

      // 8. 个人简介 (支持多行显示)
      ctx.font = '18px sans-serif';
      ctx.fillStyle = '#444444';
      ctx.textAlign = 'center';
      const bioLines = wrapText(ctx, bio, 480);
      let bioStartY = 390;
      bioLines.forEach((line, index) => {
        ctx.fillText(line, width / 2, bioStartY + index * 25);
      });

      // 9. 标签 (圆角矩形背景)
      if (tags.length > 0) {
        const tagY = bioStartY + bioLines.length * 25 + 30;
        const tagSpacing = 15;
        let currentX =
          width / 2 - (tags.length * 60 + (tags.length - 1) * tagSpacing) / 2;
        tags.forEach((tag) => {
          ctx.fillStyle = '#C1C1D4';
          ctx.beginPath();
          ctx.roundRect(currentX, tagY - 15, 60, 25, 12);
          ctx.fill();
          ctx.font = '14px sans-serif';
          ctx.fillStyle = '#05042E';
          ctx.textAlign = 'center';
          ctx.textBaseline = 'middle';
          ctx.fillText(tag, currentX + 30, tagY);
          currentX += 60 + tagSpacing;
        });
      }

      // 10. 二维码说明文字
      ctx.font = '24px sans-serif';
      ctx.fillStyle = '#8C34FF';
      ctx.textAlign = 'left';
      ctx.textBaseline = 'middle';
      ctx.fillText('扫描二维码', 50, 530);
      ctx.fillText('来协创者号小程序找到我', 50, 570);

      // 11. 二维码区域
      const qrSectionX = width - 170;
      const qrSectionY = 500;
      let qrImg = null;
      if (qrUrl) {
        qrImg = await loadImage(qrUrl, '/placeholder.png');
      }
      if (qrImg) {
        ctx.drawImage(qrImg, qrSectionX, qrSectionY, 150, 150);
      } else {
        ctx.fillStyle = '#f0f0f0';
        ctx.fillRect(qrSectionX, qrSectionY, 80, 80);
        ctx.strokeStyle = '#ddd';
        ctx.strokeRect(qrSectionX, qrSectionY, 80, 80);
      }

      // 12. 下载图片
      const imgUrl = canvas.toDataURL('image/png');
      const a = document.createElement('a');
      a.href = imgUrl;
      a.download = '个人信息卡片.png';
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      (message || antdMessage).success('图片已保存');
    } catch (e) {
      console.error('图片生成失败:', e);
      (message || antdMessage).error('图片生成失败');
    } finally {
      setDownloading(false);
    }
  };

  // 辅助函数：文本换行
  function wrapText(context, text, maxWidth) {
    const words = text.split('');
    const lines = [];
    let currentLine = '';
    for (let i = 0; i < words.length; i++) {
      const testLine = currentLine + words[i];
      const metrics = context.measureText(testLine);
      const testWidth = metrics.width;
      if (testWidth > maxWidth && currentLine !== '') {
        lines.push(currentLine);
        currentLine = words[i];
      } else {
        currentLine = testLine;
      }
    }
    lines.push(currentLine);
    return lines.slice(0, 3);
  }

  // 添加 roundRect 方法支持 (如果浏览器不支持)
  if (!CanvasRenderingContext2D.prototype.roundRect) {
    CanvasRenderingContext2D.prototype.roundRect = function (
      x,
      y,
      width,
      height,
      radius
    ) {
      this.moveTo(x + radius, y);
      this.arcTo(x + width, y, x + width, y + height, radius);
      this.arcTo(x + width, y + height, x, y + height, radius);
      this.arcTo(x, y + height, x, y, radius);
      this.arcTo(x, y, x + width, y, radius);
      this.closePath();
    };
  }

  // Popover内容
  const popoverContent = (
    <div className="flex flex-col bg-white rounded-lg text-base p-2 font-normal">
      <div
        className="flex items-center gap-2  py-2 cursor-pointer"
        onClick={() => handleCopy(window.location.href, message)}
      >
        <CopyOutlined style={{ fontSize: 16 }} />
        <span>复制链接</span>
      </div>
      <div
        className="flex items-center gap-2 py-2 cursor-pointer"
        onClick={handleSaveImage}
      >
        <PictureOutlined style={{ fontSize: 16 }} />
        <span>{downloading ? '图片生成中...' : '保存图片'}</span>
      </div>
      <div className="flex items-center gap-2 py-2">
        <WechatOutlined style={{ fontSize: 16 }} />
        <span>微信扫一扫</span>
      </div>
      <div className="flex justify-center">
        {loadingQr ? (
          <Spin />
        ) : qrUrl ? (
          <img
            src={qrUrl}
            alt="微信小程序二维码"
            style={{ width: 150, height: 150 }}
            onError={(e) => {
              e.target.src = '/placeholder.png';
            }}
          />
        ) : (
          <div
            style={{
              width: 120,
              height: 120,
              background: '#f0f0f0',
              borderRadius: 10,
            }}
          />
        )}
      </div>
    </div>
  );

  // Popover开关和二维码请求
  const handleOpenChange = async (open) => {
    if (open && !currentUser) {
      showLoginModal();
      return;
    }
    setVisible(open);
    if (open && !qrUrl && qrConfig && userInfo) {
      setLoadingQr(true);
      try {
        let rawId = userInfo.id;
        let scene = qrConfig.getScene;
        // 自动base62编码scene中的id参数
        if (typeof scene === 'function') {
          // 只对uid/postid/proposalid等做base62编码
            scene = scene(uuidToBase62(rawId));
        }
        const page = qrConfig.page;
        const qr = await (await import('../../services/shareService')).default.getWechatQrcode(scene, page);
        setQrUrl(qr.qrcode);
      } catch {
        setQrUrl('');
      } finally {
        setLoadingQr(false);
      }
    }
  };

  return (
    <Popover
      content={popoverContent}
      trigger="click"
      open={visible}
      onOpenChange={handleOpenChange}
      placement="top"
      overlayInnerStyle={{
        background: '#fff',
        borderRadius: 10,
        width: 200,
      }}
    >
      {children}
    </Popover>
  );
};

export default SharePopover;